import { config } from "../config";
import { ApiError } from "./errors";
import { callGateway, type CallerIdentity } from "./gateway";

/**
 * Subscriber cart v1 (radius-wiki subscriber-cart-v1.md §9, owner 2026-10-14):
 * a plan plus add-ons, priced by the SERVER only. radaccess
 * POST /access/operator/{ns}/cart/quote prices every line through radbilling's
 * resolver; the console shows what came back and never computes a price.
 *
 * Every JSON field name of the contract lives in this one file, so a change in
 * radaccess is one edit here.
 */
export type CartMode = "create" | "recharge" | "online";

/**
 * A price component's id: a UUID string (radbilling_component.id is
 * BINARY(16)). "" on a plan with no price row. Compared as strings, never
 * converted to a number.
 */
export type ComponentId = string;

export interface CartAddon {
  component_id: ComponentId;
  /** 1..10 on the wire; a zero is never sent. */
  quantity: number;
}

export interface CartQuoteInput {
  /** The RADIUS plan's groupname. */
  plan: string;
  addons: CartAddon[];
  mode: CartMode;
  /** The subscriber's account; recharge and online only. */
  accountid?: string;
  /** Create only: the sale's distinct installation addresses, which a per-address one-time charge is capped at (radaccess v1.189+; sent only when above 1). */
  addresses?: number;
}

export type BillingType = "RECURRING" | "ONE_TIME";

export interface CartPlan {
  groupname: string;
  /** "" when the plan has no price row. */
  component_id: ComponentId;
  name: string;
  /** "residential" (wired) or "hotspot"; "" when the plan has no price row. */
  product: string;
  /** null: no price row. */
  price: number | null;
  /** "spends": the plan is pin-backed; its line keeps its price (owner C4, corrected 2026-10-14). */
  pin: "spends" | "";
}

export interface CartOffer {
  component_id: ComponentId;
  name: string;
  billing_type: BillingType | string;
  price: number;
  effect_devices: number;
  /** What the subscriber had at the last sale; 0 when none. */
  remembered_quantity: number;
  /** "included": comes with the plan, in the cart and locked at ₹0; "optional". Priced carts, radaccess v1.174+. */
  state?: OfferState;
  /** One unit at its reference when more than `price` (radaccess v1.176+), and why. */
  list_price?: number;
  saving_reason?: string;
  /** "address": a one-time charge bought up to once per installation address of the sale (owner PC-1/PC-2, radaccess v1.189+). */
  charged_per?: string;
}

export type OfferState = "included" | "optional";

export interface CartLine {
  component_id: ComponentId;
  name: string;
  quantity: number;
  /** null: no price row. */
  amount: number | null;
  /** Comes with the plan: shown locked (radaccess v1.174+). */
  included?: boolean;
  /** RECURRING (paid again at renewal) or ONE_TIME (activation, installation). radaccess v1.176+. */
  billing_type?: BillingType | string;
  /** Priced carts, radaccess v1.176+: net + gst = amount; what it saves against list_amount, and why. */
  net?: number;
  gst?: number;
  list_amount?: number;
  saving?: number;
  saving_reason?: string;
}

export interface CartQuote {
  plan: CartPlan;
  wired: boolean;
  offered: CartOffer[];
  lines: CartLine[];
  /** null when a line has no price row: the console shows "no price listed". */
  total: number | null;
  simultaneoususe: number;
  /** Extra devices the add-ons buy (Σ effect devices × quantity): Create takes one MAC each. Absent before radaccess v1.126. */
  extradevices?: number;
  note: string;
  /** The lines' savings added up: already inside total, never deducted again (radaccess v1.176+). */
  saving?: number;
  /** The recurring lines again next term at today's prices (radaccess v1.176+). */
  renewal?: number;
}

/** The most of one add-on a cart may carry (contract: quantity 1..10). */
export const MAX_QUANTITY = 10;

/** Chosen quantities by component id, as the steppers hold them. */
export type CartSelection = Record<ComponentId, number>;

const quotePath = (ns: string) => `/operator/${encodeURIComponent(ns)}/cart/quote`;

/** The selection as the wire's `addons`: zeros dropped, 1..10, ordered by id. */
export function cartAddons(sel: CartSelection): CartAddon[] {
  return Object.entries(sel)
    .map(([id, q]) => ({ component_id: id, quantity: Math.min(MAX_QUANTITY, Math.max(0, Math.trunc(q))) }))
    .filter((a) => a.quantity > 0 && a.component_id !== "")
    .sort((a, b) => (a.component_id < b.component_id ? -1 : a.component_id > b.component_id ? 1 : 0));
}

/** The JSON body of a quote: exactly the contract's fields. */
export function quoteBody(input: CartQuoteInput): CartQuoteInput {
  const body: CartQuoteInput = { plan: input.plan, addons: input.addons, mode: input.mode };
  if (input.accountid) body.accountid = input.accountid;
  // Older radaccess refuses an unknown field: one address is its default anyway.
  if (input.addresses && input.addresses > 1) body.addresses = input.addresses;
  return body;
}

export async function quote(identity: CallerIdentity, ns: string, input: CartQuoteInput, signal?: AbortSignal): Promise<CartQuote> {
  const r = await callGateway<CartQuote | null>(identity, config().gatewayBase, quotePath(ns), { method: "POST", body: quoteBody(input), ...(signal ? { signal } : {}) });
  const q = r.data;
  if (!q || !q.plan) throw new ApiError("unavailable", "The price server returned no quote");
  return { ...q, offered: q.offered ?? [], lines: q.lines ?? [] };
}

/** One-time add-ons are sold only at Create subscriber (contract §3). */
export function isOneTime(o: CartOffer): boolean {
  return o.billing_type === "ONE_TIME";
}

/** A one-time charge made once per installation address (installation, a router sold outright). */
export function isPerAddress(o: CartOffer): boolean {
  return isOneTime(o) && o.charged_per === "address";
}

/**
 * How many of an add-on a sale may hold (owner 2026-10-27, PC-1/PC-2): a
 * one-time charge once, or once per installation address when it is charged
 * per address; a recurring add-on up to MAX_QUANTITY.
 */
export function maxQuantityOf(o: CartOffer, addresses = 1): number {
  if (isPerAddress(o)) return Math.max(1, addresses);
  return isOneTime(o) ? 1 : MAX_QUANTITY;
}

/**
 * The selection after the sale's installation addresses went from `before`
 * to `after`: a chosen per-address charge follows the count (one
 * installation per address), still lowerable by hand; nothing else moves.
 */
export function followAddresses(sel: CartSelection, offers: CartOffer[], before: number, after: number): CartSelection {
  if (before === after) return sel;
  let out = sel;
  for (const o of offers) {
    const n = sel[o.component_id] ?? 0;
    if (isPerAddress(o) && n > 0 && n !== Math.max(1, after)) out = { ...out, [o.component_id]: Math.max(1, after) };
  }
  return out;
}

/**
 * The add-ons a screen may show. The server already offers nothing for a
 * hotspot plan and no one-time items outside create; this keeps the console
 * from showing them should it ever be sent more.
 */
export function visibleOffers(q: CartQuote | null, mode: CartMode): CartOffer[] {
  if (!q) return [];
  // Owner D6 (2026-10-26): a hotspot plan may carry extra devices; the server offers only those.
  return q.offered.filter((o) => (mode === "create" || !isOneTime(o)) && (q.wired || o.effect_devices > 0));
}

/** An offer that comes with the plan: shown ticked and locked, never a stepper. */
export function isIncluded(o: CartOffer): boolean {
  return o.state === "included";
}

/** Owner C3, widened by D6: a hotspot plan sells alone, extra devices aside. "" when there is nothing to say. */
export function hotspotMessage(q: CartQuote | null): string {
  if (!q || q.wired || q.plan.product !== "hotspot") return "";
  return q.offered.some((o) => o.effect_devices > 0) ? "Hotspot plans take only extra devices" : "Hotspot plans are sold without add-ons";
}

const SAVING_WORDS: Record<string, string> = { term: "longer term", included: "included with the plan", promotion: "festive price" };

/** "longer term, included with the plan": the server's reasons in words. */
export function savingWords(reason: string | undefined): string {
  return (reason ?? "")
    .split(",")
    .map((r) => SAVING_WORDS[r.trim()] ?? r.trim())
    .filter(Boolean)
    .join(", ");
}

/** A line's saving in words, "" when it saves nothing: "₹2,994 → saves ₹295 (longer term)". */
export function lineSavingLabel(l: CartLine): string {
  if (!l.saving || l.saving <= 0) return "";
  const why = savingWords(l.saving_reason);
  const was = l.list_amount ? `${rupees(l.list_amount)} → ` : "";
  return `${was}saves ${rupees(l.saving)}${why ? ` (${why})` : ""}`;
}

/** The cart's GST, added up from the server's per-line figures; null when a line has none (an older radaccess). */
export function totalGst(q: CartQuote): number | null {
  if (!q.lines.length || q.lines.some((l) => l.amount != null && l.gst == null)) return null;
  const paise = q.lines.reduce((s, l) => s + Math.round((l.gst ?? 0) * 100), 0);
  return paise / 100;
}

/** Recharge pre-ticks what the subscriber had last time (owner O1). */
export function rememberedSelection(q: CartQuote | null, mode: CartMode = "recharge"): CartSelection {
  const sel: CartSelection = {};
  for (const o of visibleOffers(q, mode)) if (o.remembered_quantity > 0) sel[o.component_id] = Math.min(MAX_QUANTITY, o.remembered_quantity);
  return sel;
}

/** "₹1,000" or "₹380.51": the server's number, formatted, never recomputed. */
export function rupees(n: number): string {
  return `₹${new Intl.NumberFormat("en-IN", { minimumFractionDigits: Number.isInteger(n) ? 0 : 2, maximumFractionDigits: 2 }).format(n)}`;
}

export const NO_PRICE = "no price listed";

/** A server amount in words: null means no price row. */
export function priceLabel(n: number | null | undefined): string {
  return n == null ? NO_PRICE : rupees(n);
}

/** A line's amount: the server's number, the pin-backed plan line included (owner C4, corrected). */
export function lineAmountLabel(_q: CartQuote, l: CartLine): string {
  return priceLabel(l.amount);
}

/** The plan's line in a PIN site: it keeps its price and carries a "uses 1 pin" chip. */
export function isPinLine(q: CartQuote, l: CartLine): boolean {
  return q.plan.pin === "spends" && q.plan.component_id !== "" && l.component_id === q.plan.component_id;
}

export const PIN_CHIP = "uses 1 pin";

/** The plan's own line when the server's lines do not carry it (a plan with no price row). */
export function planLineMissing(q: CartQuote): boolean {
  return !q.lines.some((l) => l.component_id === q.plan.component_id && q.plan.component_id !== "");
}

/** radaccess refuses a recharge whose amount no longer equals the total: 409 "price changed — reload". */
export function isPriceChanged(e: unknown): boolean {
  return e instanceof ApiError && e.status === 409 && /price changed/i.test(e.message);
}

/** The new total a 409 "price changed" carries ({"error", "total"}), or null. */
export function priceChangedTotal(e: unknown): number | null {
  if (!isPriceChanged(e)) return null;
  const b = (e as ApiError).body;
  const t = b && typeof b === "object" && "total" in b ? (b as { total?: unknown }).total : null;
  return typeof t === "number" ? t : null;
}

/** With a quote, the amount recorded must equal its total; "" when it does. */
export function amountMatchesTotal(amount: string, total: number): string {
  const a = Number(amount.trim());
  if (amount.trim() === "" || !Number.isFinite(a) || Math.abs(a - total) > 0.001) return `Amount must equal the total ${rupees(total)}`;
  return "";
}
